/**
 * ================================================================
 * UI COMPONENT: NotificationsPanel
 * ================================================================
 *
 * @component        NotificationsPanel
 * @description      Main notifications panel with tabs and infinite scroll
 * @category         UI Component
 *
 * @props
 * - uid: string - User identifier for fetching notifications
 * - className?: string - Additional CSS classes
 * - onClose?: () => void - Callback when closing panel
 *
 * @styling
 * - Base: Tailwind CSS utility classes
 * - Responsive: Mobile-optimized layout
 * - Scrolling: Infinite scroll with smooth animations
 *
 * @accessibility
 * - ARIA labels and roles
 * - Keyboard navigation support
 * - Screen reader friendly content
 *
 * @usage
 * ```tsx
 * <NotificationsPanel
 *   uid="VJ"
 *   onClose={handleClose}
 * />
 * ```
 * ================================================================
 */

import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, InboxIcon, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { NotificationItem } from '@/components/ui/NotificationItem';
import { NotificationTabs } from '@/components/ui/NotificationTabs';
import { useIntegratedNotifications } from '@/hooks/useIntegratedNotifications';
import { cn } from '@/lib/utils/utils';
import { getNotificationRoute } from '@/utils/notification-routes/routes';

interface NotificationsPanelProps {
  className?: string;
  onClose?: () => void;
  onViewAllClick?: () => void;
  uid: string;
}

export function NotificationsPanel({
  uid,
  onClose,
  onViewAllClick,
  className,
}: NotificationsPanelProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ================================================================
  // NOTIFICATIONS HOOK
  // ================================================================
  //
  // Manages all notification state, API calls, and pagination
  // Provides data and actions for the component
  // ================================================================

  const {
    activeTab,
    notifications,
    isLoading,
    hasMore,
    unreadCount,
    loadMore,
    changeTab,
    markAsRead,
  } = useIntegratedNotifications(uid, {
    enableSocket: true,
    enableFirebase: true,
    autoRequestPermission: false,
    pageSize: 10,
    onFirebaseMessage: (payload) => {
      console.log('🔔 Received Firebase notification in panel:', payload);
      // The notification popup will be handled by fcmTokenManager
      // This callback is for additional UI updates if needed
    },
  });

  // ================================================================
  // INFINITE SCROLL LOGIC
  // ================================================================
  //
  // Detects when user scrolls near bottom and loads more data
  // Uses Intersection Observer for performance
  // ================================================================

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollPercentage = scrollTop / (scrollHeight - clientHeight);

    // Load more when user scrolls to 80% of content
    if (scrollPercentage > 0.8) {
      loadMore();
    }
  }, [isLoading, hasMore, loadMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ================================================================
  // EVENT HANDLERS
  // ================================================================
  //
  // Handle user interactions and navigation
  // Tab changes, mark as read, and panel actions
  // ================================================================

  const handleTabChange = useCallback(
    (newTab: string) => {
      changeTab(newTab as any);
    },
    [changeTab],
  );

  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      markAsRead(notificationId);
    },
    [markAsRead],
  );

  const handleViewAllClick = useCallback(() => {
    // Close current panel and open drawer
    if (onClose) {
      onClose();
    }
    if (onViewAllClick) {
      onViewAllClick();
    }
  }, [onClose, onViewAllClick]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className={cn(
        // ================================================================
        // PANEL CONTAINER STYLES - Using design system
        // ================================================================
        //
        // Layout: Fixed width with responsive adjustments
        // Background: Card background with proper shadows
        // Borders: Using design system border colors
        // ================================================================
        'flex h-[500px] w-80 flex-col overflow-hidden',
        'rounded-lg border bg-card shadow-md',
        className,
      )}
      role="dialog"
      aria-label="Notifications panel"
    >
      {/* ================================================================
           PANEL HEADER
           ================================================================
           
           Header with title, unread count, and close action
           Consistent styling with rest of application
           ================================================================ */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-card-foreground">Notifications</h3>
          </div>
          {unreadCount > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="rounded-full bg-primary/10 px-2 py-1"
            >
              <span className="text-xs font-medium text-primary">{unreadCount} new</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* ================================================================
           TAB NAVIGATION
           ================================================================
           
           Tab navigation for filtering notifications by status
           Integrated with notification count display
           ================================================================ */}
      <NotificationTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        unreadCount={unreadCount}
        type="NotificationPanel"
      />

      {/* ================================================================
           NOTIFICATIONS CONTENT
           ================================================================
           
           Main content area with notifications list and scroll
           Handles loading, empty, and error states
           ================================================================ */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Loading state for initial load */}
        {isLoading && notifications.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm">Loading notifications...</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && notifications.length === 0 && (
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-muted-foreground">
              <InboxIcon className="h-12 w-12 text-muted-foreground/40" />
              <div className="text-center">
                <p className="text-sm font-medium">No notifications</p>
                <p className="text-xs">
                  {activeTab === 'All'
                    ? "You're all caught up!"
                    : `No ${activeTab.toLowerCase()} notifications`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Notifications list */}
        {notifications.length > 0 && (
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            role="tabpanel"
            id={`panel-${activeTab}`}
            aria-labelledby={`tab-${activeTab}`}
          >
            <div className="space-y-1 p-2">
              {notifications.map((notification, index) => (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {(() => {
                    // Build validated route; only link if valid.
                    const route = getNotificationRoute(notification.app, notification.feature);
                    const content = (
                      <NotificationItem
                        notification={notification}
                        onMarkAsRead={handleMarkAsRead}
                        type="panel"
                      />
                    );
                    if (route) {
                      return (
                        <Link
                          to={route}
                          onClick={() => {
                            const isUnread = notification.status === 'UNREAD';
                            if (isUnread && markAsRead) {
                              markAsRead(notification.id);
                            }
                          }}
                          className="block"
                        >
                          {content}
                        </Link>
                      );
                    }
                    return (
                      <div
                        onClick={() => {
                          const isUnread = notification.status === 'UNREAD';
                          if (isUnread && markAsRead) {
                            markAsRead(notification.id);
                          }
                        }}
                        className="block cursor-pointer"
                      >
                        {content}
                      </div>
                    );
                  })()}
                </motion.div>
              ))}

              {/* Loading indicator for pagination */}
              {isLoading && notifications.length > 0 && (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ================================================================
           PANEL FOOTER
           ================================================================
           
           Footer with action button to view all notifications
           Consistent with application button styles
           ================================================================ */}
      {notifications.length > 0 && (
        <>
          <div className="h-px bg-border" />
          <div className="p-3">
            <Button onClick={handleViewAllClick} className="w-full">
              View All Notifications
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
}

/**
 * ================================================================
 * END OF COMPONENT: NotificationsPanel
 * ================================================================
 *
 * @exports          NotificationsPanel - Main notifications component
 * @dependencies     React hooks, Framer Motion, custom hooks, UI components
 * @usedBy           Header component for dropdown notifications
 *
 * @performance
 * - Infinite scroll: Optimized with Intersection Observer
 * - Memoization: Child components memoized to prevent re-renders
 * - Animation: Lightweight animations with Framer Motion
 *
 * ================================================================
 */
